import React from 'react';
import {
  buildPlanViewModel,
  planPreviewPlaceFromGround,
  planPreviewZoneLabel,
} from './plantingPlanDisplay';

export function PlantingPlanStep({
  answers,
  place = null,
  acres = 0,
  draftSoilPh = '',
  orderEnabled = false,
  onOrderClick,
  preferKey = '',
  onSelectAlt,
  readOnly = false,
  loadPlan,
  onPreview,
}) {
  const [model, setModel] = React.useState(null);
  const [status, setStatus] = React.useState('loading');

  React.useEffect(() => {
    if (typeof loadPlan !== 'function') {
      setStatus('error');
      return undefined;
    }
    let cancelled = false;
    setStatus('loading');
    setModel(null);
    (async () => {
      try {
        const payload = await loadPlan({
          acres,
          wizardAnswers: answers || {},
          place: planPreviewPlaceFromGround(place, draftSoilPh),
          blend: preferKey,
        });
        if (cancelled) return;
        onPreview?.(payload || null);
        const next = buildPlanViewModel(payload, {
          orderEnabled,
          zone: planPreviewZoneLabel(place),
        });
        setModel(next);
        setStatus(next ? 'ready' : 'error');
      } catch (err) {
        console.error('Plan preview failed', err);
        if (!cancelled) {
          onPreview?.(null);
          setModel(null);
          setStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [answers, place, acres, draftSoilPh, preferKey, orderEnabled, loadPlan, onPreview]);

  if (status === 'loading') {
    return (
      <section className="plant-plan" data-testid="plant-plan-loading">
        <p className="plant-plan-hero__blurb">Reading the blend…</p>
      </section>
    );
  }

  if (status === 'error' || !model) {
    return (
      <section className="plant-plan" data-testid="plant-plan-error">
        <p className="plant-plan-hero__blurb">
          Unable to read this plan. Go back and try again.
        </p>
      </section>
    );
  }

  return (
    <section className="plant-plan" data-testid="plant-plan-step">
      <div className="plant-plan-hero">
        <div className="plant-plan-hero__copy">
          <div className="plant-plan-hero__kicker">PLANT THIS</div>
          <h2 className="plant-plan-hero__title">{model.recName}</h2>
          <p className="plant-plan-hero__blurb">{model.recBlurb}</p>
          <div className="plant-plan-tags">
            {model.recTags.map((tag) => (
              <span
                key={tag.label}
                className="plant-plan-tag"
                style={{ background: tag.bg, color: tag.fg }}
              >
                {tag.label}
              </span>
            ))}
          </div>
        </div>
        <div className="plant-plan-hero__match">
          <div className="plant-plan-hero__match-kicker">
            MATCH FOR {model.acresLabel} AC · ZONE {model.zone}
          </div>
          <div className="plant-plan-hero__score">
            <span className="plant-plan-hero__score-num">{model.recMatch}</span>
            <span className="plant-plan-hero__score-out">OUT OF 100</span>
          </div>
          <div className="plant-plan-hero__bar" aria-hidden="true">
            <div style={{ width: model.recMatchPct }} />
          </div>
          <div className="plant-plan-hero__note">{model.recMatchNote}</div>
        </div>
      </div>

      <div className="plant-plan__grid">
        <div className="plant-plan-card">
          <div className="plant-plan-card__head">
            <span>WHY THIS BLEND</span>
            <span className="plant-plan-card__accent">{model.reasonCount}</span>
          </div>
          <div className="plant-plan-reasons">
            {model.reasons.map((reason) => (
              <div
                key={reason.id || reason.tag}
                className={`plant-plan-reason${reason.alt ? ' is-alt' : ''}`}
                data-testid={`plant-plan-reason-${reason.id || String(reason.tag).toLowerCase()}`}
              >
                <span
                  className="plant-plan-reason__chip"
                  style={{ background: reason.chip, color: reason.chipFg }}
                >
                  {reason.tag}
                </span>
                <span className="plant-plan-reason__body">
                  <span className="plant-plan-reason__title">{reason.title}</span>
                  <span className="plant-plan-reason__text">{reason.text}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="plant-plan-side">
          <div className="plant-plan-card">
            <div className="plant-plan-card__head">
              <span>WHAT TO BUY · {model.acresLabel} ACRES</span>
              <span className="plant-plan-card__accent">{model.totalLbs} LBS</span>
            </div>
            <div className="plant-plan-cart">
              {model.cart.map((item) => (
                <div
                  key={item.name}
                  className={`plant-plan-cart__row${item.alt ? ' is-alt' : ''}`}
                >
                  <span className="plant-plan-cart__main">
                    <span className="plant-plan-cart__name">{item.name}</span>
                    <span className="plant-plan-cart__rate">{item.rate}</span>
                  </span>
                  <span className="plant-plan-cart__qty">
                    <span className="plant-plan-cart__amount">{item.qty}</span>
                    <span className="plant-plan-cart__bags">{item.bags}</span>
                  </span>
                </div>
              ))}
              <div className="plant-plan-cart__total">
                <span>ROUGH COST</span>
                <strong>{model.costTotal}</strong>
              </div>
            </div>
          </div>

          <div className="plant-plan-card">
            <div className="plant-plan-card__head">IF YOU&apos;D RATHER</div>
            <div className="plant-plan-alts">
              {model.alts.map((alt) => {
                const canSwitch = Boolean(alt.selectable && alt.key && onSelectAlt && !readOnly);
                const AltTag = canSwitch ? 'button' : 'div';
                return (
                  <AltTag
                    key={alt.key || alt.name}
                    type={canSwitch ? 'button' : undefined}
                    className={`plant-plan-alt${canSwitch ? ' is-selectable' : ''}`}
                    onClick={canSwitch ? () => onSelectAlt(alt.key) : undefined}
                    data-testid={alt.key ? `plant-plan-alt-${alt.key}` : `plant-plan-alt-${String(alt.name).slice(0, 24)}`}
                  >
                    <div className="plant-plan-alt__row">
                      <span className="plant-plan-alt__name">{alt.name}</span>
                      <span className="plant-plan-alt__match">
                        {canSwitch ? 'SWITCH' : alt.match}
                      </span>
                    </div>
                    <div className="plant-plan-alt__bar" aria-hidden="true">
                      <div style={{ width: alt.pct, background: alt.fill }} />
                    </div>
                    <p className="plant-plan-alt__note">{alt.note}</p>
                  </AltTag>
                );
              })}
            </div>
          </div>

          <div className="plant-plan-order">
            <div className="plant-plan-order__kicker">GET IT DELIVERED</div>
            <p className="plant-plan-order__note">{model.orderNote}</p>
            <button
              type="button"
              className="plant-plan-order__btn"
              disabled={model.orderDisabled}
              onClick={onOrderClick}
              data-testid="plant-plan-order-btn"
            >
              {model.orderLabel}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
